import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from "@angular/core";
import { Data, TrainData } from "../models/trainDataModel";
import { Direction, TrainType, CurrentLocationStatus, AnnouncementAgeLevel } from "../models/train-enums";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Store } from "@ngrx/store";
import { Actions, ofType } from "@ngrx/effects";
import {
    selectFetchingTrainData,
    selectMessageData,
    selectTrainData
} from "../store/home/home.selector";
import { pollAllTrainDataSync, stopPollingTrainDataSync, trainDataError, messageDataError } from "../store/home/home.action";
import { MessageData } from "../models/messageDataModel";
import { BreakpointObserver } from '@angular/cdk/layout';
import { BrowserUtilsService } from "../service/browser-utils.service";
import { ActivatedRoute } from "@angular/router";

@Component({
    selector: "app-home",
    templateUrl: "./home.component.html",
    styleUrl: "./home.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
    trainData$: Observable<TrainData>;
    trainData = {} as TrainData;
    northboundTrains: Data[] = [];
    southboundTrains: Data[] = [];
    fetchingTrainData$: Observable<boolean>;
    messageData$: Observable<MessageData[]>;
    messageData: MessageData[] = [];

    readonly CurrentLocationStatus = CurrentLocationStatus;
    readonly TrainType = TrainType;

    timeOfUpdate: string = "";

    isInIframe = false;

    isMobileSize: boolean = false;

    isFetchingTrainData: boolean = true;
    hasDataError: boolean = false;
    private destroy$ = new Subject<void>();
    private refreshTimer?: number;

    constructor(
        private store: Store, 
        private actions$: Actions,
        private breakpointObserver: BreakpointObserver, 
        private browserUtils: BrowserUtilsService,
        private activatedRoute: ActivatedRoute,
    ) {
        this.trainData$ = this.store.select(selectTrainData)
        this.fetchingTrainData$ = this.store.select(selectFetchingTrainData)
        this.messageData$ = this.store.select(selectMessageData)

        this.breakpointObserver.observe(['(max-width: 768px)'])
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.isMobileSize = this.breakpointObserver.isMatched('(max-width: 768px)');
            });
    }

    ngOnInit(): void {
        try {
            this.isInIframe = this.browserUtils.isInIframe();
            this.store.dispatch(pollAllTrainDataSync());

            // Auto-refresh after 12 hours
            this.refreshTimer = window.setTimeout(() => {
                window.location.reload();
            }, 12 * 60 * 60 * 1000)

            this.fetchingTrainData$
                .pipe(takeUntil(this.destroy$))
                .subscribe(fetching => {
                    this.isFetchingTrainData = fetching;
                });

            this.trainData$
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (trainData) => {
                        this.trainData = trainData;
                        this.hasDataError = false;
                        this.northboundTrains = (trainData.data ?? []).filter(train => train.direction === Direction.NORTH);
                        this.southboundTrains = (trainData.data ?? []).filter(train => train.direction === Direction.SOUTH);

                        const now = new Date();
                        this.timeOfUpdate = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
                    },
                    error: (error) => {
                        console.error('Error receiving train data:', error);
                    }
                });

            this.messageData$
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (messageData) => {
                        this.messageData = messageData;
                    },
                    error: (error) => {
                        console.error('Error receiving message data:', error);
                    }
                });

            

            this.activatedRoute.queryParams
                .pipe(takeUntil(this.destroy$))
                .subscribe(params => {
                    let theme = 'dark';

                    if (params['darktheme'] !== undefined) {
                        const isDark = params['darktheme'] === 'true';
                        theme = isDark ? 'dark' : 'light';
                    }

                    this.browserUtils.setDocumentAttribute('data-theme', theme);
                });

            this.actions$.pipe(ofType(trainDataError), takeUntil(this.destroy$)).subscribe((action) => {
                console.error('Train data error received:', action.error);
                this.hasDataError = true;
            });

            this.actions$.pipe(ofType(messageDataError), takeUntil(this.destroy$)).subscribe((action) => {
                console.error('Message data error received:', action.error);
            });
        } catch (error) {
            console.error('Error during component initialization:', error);
        }
    }

    public get boardRowCount(): number {
        return Math.max(this.northboundTrains.length, this.southboundTrains.length, 1);
    }

    public trackTrain(data: Data): string {
        return `${data.trainNumber}|${data.suffix}|${data.announcedTrainNumber}`;
    }

    public customRowCss(data: Data): string[] {
        const cssClasses: string[] = ['train-tablerow--standard'];
    
        if (data.trainType === TrainType.SERVICETRAIN) {
            cssClasses.push('train-tablerow--service-white');
        } else if (data.delay >= 3) {
            cssClasses.push('train-tablerow--delay-red');
        }
    
        return cssClasses;
    }
    

    public openMessage(message: MessageData) {
        this.browserUtils.postMessageToParent({
          type: 'open-message',
          messageId: message.id
        });
    }

    public getTrainTooltipText(direction: Direction): string {
        return direction === Direction.NORTH ? 'Tåg norrut' : 'Tåg söderut';
    }

    public getTooltipPosition(): 'above' | 'below' {
        return this.isMobileSize ? 'above' : 'below';
    }

    public getFullLocationName(location: string): string {
        return this.trainData.data?.find(train => train.location === location)?.advertisedLocationName ?? location;
    }

    public getFullLocationNameWithArrival(data: Data): string {
        const fullLocationName = this.getFullLocationName(data.location);
        const hasAsterisk = data.currentLocationStatus === CurrentLocationStatus.ARRIVED; 

        return hasAsterisk ? `Ankommit ${fullLocationName}` : `Avgått ${fullLocationName}`;
    }

    public getTrainInfo(data: Data): { displayText: string, tooltipText: string } {
        const asterisks = this.getAnnouncementAgeSign(data);
        const delay = Number.isFinite(data.delay) ? data.delay : 0;
        const plusSign = delay > 0 ? "+" : "";

        const absoluteDelay = Math.abs(delay);
        let tooltipText: string;

        if (absoluteDelay === 0) {
            tooltipText = "Rätt tid";
        } else if (delay > 0) {
            tooltipText = `${absoluteDelay} minuter sent`;
        } else {
            tooltipText = `${absoluteDelay} minuter tidigt`;
        }

        if (asterisks === '*') {
            tooltipText += `, tåget har ännu inte fått ny tid`;
        } else if (asterisks === '**') {
            tooltipText += `, tåget har ännu inte fått ny tid och verkar stå stilla`;
        }

        return {
            displayText: `${asterisks}${plusSign}${delay}`,
            tooltipText: tooltipText
        };
    }

    private getAnnouncementAgeSign(data: Data): string {
        switch (data.announcementAgeLevel) {
            case AnnouncementAgeLevel.LEVEL1:
                return "";
            case AnnouncementAgeLevel.LEVEL2:
                return "*";
            case AnnouncementAgeLevel.LEVEL3:
                return "**";
            default:
                return "";
        }
    }

    ngOnDestroy(): void {
        this.store.dispatch(stopPollingTrainDataSync());
        this.destroy$.next();
        this.destroy$.complete();

        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
    }
}